import { React, useState, useEffect } from 'react';
import { Tooltip, Modal, Form, Input, Upload, Button, message } from 'antd';
import { BugOutlined, UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import asyncFetch from '../utils/asyncFetch';

const SubmitBug = () => {
    const { t } = useTranslation();
    const [isTooltipVisible, setIsTooltipVisible] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [base64Image, setBase64Image] = useState(null);

    const currentPath = window.location.pathname;

    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(file);
        });
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsTooltipVisible(false);
        }, 3000);

        return () => clearTimeout(timer);
    }, []);

    const handleUpload = async (file) => {
        const isImage = file.type.startsWith('image/');
        const isSmallEnough = file.size / 1024 / 1024 < 5;

        if (!isImage) {
            message.error('只能上传图片文件！');
            return Upload.LIST_IGNORE;
        }

        if (!isSmallEnough) {
            message.error('图片大小不能超过 5MB！');
            return Upload.LIST_IGNORE;
        }

        try {
            const base64 = await convertToBase64(file); // 转换为 Base64
            setBase64Image(base64); // 保存Base64字符串
        } catch (error) {
            message.error('Failed to upload image!');
        }
        return false
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields(); // 验证表单
            const dataToSubmit = {
                ...values,
                screenshot: base64Image, // 添加Base64图片
            };
            asyncFetch('POST', '/issue', dataToSubmit,
                (res) => {
                    if (res.code === 200) {
                        message.success('The issue has been successfully submitted!');
                        handleReset();
                    } else {
                        message.error(res.msg);
                    }
                },
            {}, 'cors', {}, false);
        } catch (errorInfo) {
            console.error('表单验证失败:', errorInfo);
        }
    };

    const handleReset = () => {
        form.resetFields();
        setBase64Image(null);
        setIsModalVisible(false);
    };

    return (
        <>
            <Tooltip
                title={t('submit_bug_title')}
                visible={isTooltipVisible}
                onVisibleChange={setIsTooltipVisible}
                placement="top"
            >
                <div
                    style={{
                        position: 'fixed',
                        bottom: 50,
                        right: 50,
                        width: 50,
                        height: 50,
                        backgroundColor: '#f5222d',
                        borderRadius: '50%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                        cursor: 'pointer',
                        color: '#fff',
                    }}
                    onClick={() => setIsModalVisible(true)}
                >
                    <BugOutlined style={{ fontSize: '20px' }} />
                </div>
            </Tooltip>

            <Modal
                title={t('submit_bug_title')}
                visible={isModalVisible}
                onCancel={handleReset}
                onOk={handleSubmit}
                okText={t('submit_bug_submit')}
                cancelText={t('submit_bug_cancel')}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        path: currentPath,
                    }}
                >
                    <Form.Item
                        label={t('submit_bug_email')}
                        name="email"
                        rules={[
                            { required: true, message: 'Please enter your email address!' },
                            { type: 'email', message: 'Please enter a valid email address!' },
                        ]}
                    >
                        <Input placeholder="Please enter your email address so we can reply to you later" />
                    </Form.Item>

                    <Form.Item label={t('submit_bug_current_path')} name="path">
                        <Input readOnly />
                    </Form.Item>

                    <Form.Item
                        label={t('submit_bug_description')}
                        name="description"
                        rules={[{ required: true, message: 'Please enter a description of the issue!' }]}
                    >
                        <Input.TextArea rows={4} placeholder="Please describe the issue or suggestion you encountered" />
                    </Form.Item>

                    <Form.Item
                        label={t('submit_bug_upload')}
                        name="screenshot"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => e?.fileList}
                    >
                        <Upload
                            accept="image/*"
                            maxCount={1}
                            beforeUpload={handleUpload}
                            listType="picture"
                        >
                            <Button icon={<UploadOutlined />}>Click to Upload</Button>
                        </Upload>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    )
}

export default SubmitBug;
